/* eslint-disable */
import React, {FC, useMemo, useState, useCallback, useRef} from 'react';
import {useRecoilValue} from 'recoil';
import {langState} from '../../state/activities';
import {getMins} from '../../utils/statisticsCounter';

interface Props {
  arr: any;
  showRulers?: boolean;
}

const TOTAL_MINS = 1440;

const DIMENSION_COLORS: Record<string, string> = {
  activities: '#1e3a8a',
  places: '#047857',
  togetherness: '#9333ea',
  mood: '#d97706',
  pain: '#dc2626',
};

const DIMENSION_LABELS: Record<string, {SV: string; EN: string}> = {
  activities: {SV: 'Aktiviteter', EN: 'Activities'},
  places: {SV: 'Plats', EN: 'Place'},
  togetherness: {SV: 'Samvaro', EN: 'Togetherness'},
  mood: {SV: 'Sinnesstämning', EN: 'Mood'},
  pain: {SV: 'Smärta', EN: 'Pain'},
};

interface Segment {
  startMin: number;
  endMin: number;
  label: string;
  intensity?: number;
}

interface Track {
  key: string;
  label: string;
  dimension: string;
  segments: Segment[];
}

interface Group {
  dimension: string;
  tracks: Track[];
}

interface SeriesEntry {
  startMin: number;
  endMin: number;
  value: string;      // the track key (category / where / with / feeling / pain level)
  label: string;      // display string for tooltip
  dimension: string;
}

function buildSeriesEntries(
  series: any[] | undefined,
  valueField: string,
  dimension: string,
  labelFn?: (entry: any) => string,
): SeriesEntry[] {
  if (!series || series.length === 0) return [];
  const entries: SeriesEntry[] = [];
  for (let i = 0; i < series.length; i++) {
    const e = series[i];
    const rawValue = e[valueField];
    if (!rawValue || rawValue === 'Inget Loggat') continue;
    const startMin = getMins(e.start);
    const endMin = i + 1 < series.length ? getMins(series[i + 1].start) : TOTAL_MINS;
    if (endMin <= startMin) continue;
    entries.push({
      startMin,
      endMin,
      value: String(rawValue).toLowerCase(),
      label: labelFn ? labelFn(e) : String(rawValue),
      dimension,
    });
  }
  return entries;
}

function buildPainEntries(painArr: any[] | undefined): SeriesEntry[] {
  if (!painArr || painArr.length === 0) return [];
  const entries: SeriesEntry[] = [];
  for (let i = 0; i < painArr.length; i++) {
    const level = parseInt(painArr[i].level);
    if (!level || level <= 0) continue;
    const startMin = getMins(painArr[i].start);
    const endMin = i + 1 < painArr.length ? getMins(painArr[i + 1].start) : TOTAL_MINS;
    if (endMin <= startMin) continue;
    entries.push({
      startMin,
      endMin,
      value: 'pain-level',
      label: `Smärta ${level}/10`,
      dimension: 'pain',
    });
  }
  return entries;
}

function buildTracksFromSeries(
  series: any[] | undefined,
  valueField: string,
  dimension: string,
): Track[] {
  if (!series || series.length === 0) return [];
  const trackMap = new Map<string, Track>();
  for (let i = 0; i < series.length; i++) {
    const entry = series[i];
    const rawValue = entry[valueField];
    if (!rawValue || rawValue === 'Inget Loggat') continue;
    const startMin = getMins(entry.start);
    const endMin = i + 1 < series.length ? getMins(series[i + 1].start) : TOTAL_MINS;
    if (endMin <= startMin) continue;
    const key = String(rawValue).toLowerCase();
    if (!trackMap.has(key)) {
      trackMap.set(key, {key, label: String(rawValue), dimension, segments: []});
    }
    trackMap.get(key)!.segments.push({
      startMin,
      endMin,
      label: entry.activity ? `${rawValue} – ${entry.activity}` : String(rawValue),
    });
  }
  return Array.from(trackMap.values());
}

function buildPainTrack(painArr: any[] | undefined): Track[] {
  if (!painArr || painArr.length === 0) return [];
  const segments: Segment[] = [];
  for (let i = 0; i < painArr.length; i++) {
    const level = parseInt(painArr[i].level);
    if (!level || level <= 0) continue;
    const startMin = getMins(painArr[i].start);
    const endMin = i + 1 < painArr.length ? getMins(painArr[i + 1].start) : TOTAL_MINS;
    if (endMin <= startMin) continue;
    segments.push({
      startMin,
      endMin,
      label: `Smärta ${level}/10`,
      intensity: Math.max(0.2, Math.min(1, level / 10)),
    });
  }
  if (segments.length === 0) return [];
  return [{key: 'pain-level', label: '1-10', dimension: 'pain', segments}];
}

// buildActivityEntries removed – now using generic buildSeriesEntries

const HVBCombinedGraph: FC<Props> = ({arr, showRulers = false}) => {
  const lang = useRecoilValue(langState) as 'SV' | 'EN';
  const [hover, setHover] = useState<{x: number; y: number; text: string} | null>(null);
  const [focusDimension, setFocusDimension] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [ruler1Y, setRuler1Y] = useState<number | null>(null);
  const [ruler2Y, setRuler2Y] = useState<number | null>(null);
  const [draggingRuler, setDraggingRuler] = useState<1 | 2 | null>(null);

  const nonActivityGroups: Group[] = useMemo(() => {
    return [
      {dimension: 'places', tracks: buildTracksFromSeries(arr.places, 'where', 'places')},
      {dimension: 'togetherness', tracks: buildTracksFromSeries(arr.togetherness, 'with', 'togetherness')},
      {dimension: 'mood', tracks: buildTracksFromSeries(arr.mood, 'feeling', 'mood')},
    ].filter((g) => g.tracks.length > 0);
  }, [arr]);

  const allGroups = useMemo(() => {
    const actTracks = buildTracksFromSeries(arr.activities, 'category', 'activities');
    const g: Group[] = [];
    if (actTracks.length > 0) g.push({dimension: 'activities', tracks: actTracks});
    g.push(...nonActivityGroups);
    return g;
  }, [arr, nonActivityGroups]);

  // Build ordered entries for ALL dimensions (used to draw connected lines)
  const dimensionEntries: Record<string, SeriesEntry[]> = useMemo(() => ({
    activities: buildSeriesEntries(arr.activities, 'category', 'activities',
      (e) => `${e.category}${e.activity ? ' – ' + e.activity : ''}`),
    places: buildSeriesEntries(arr.places, 'where', 'places'),
    togetherness: buildSeriesEntries(arr.togetherness, 'with', 'togetherness'),
    mood: buildSeriesEntries(arr.mood, 'feeling', 'mood'),
  }), [arr]);

  // Layout
  const PADDING_LEFT = 60;
  const PADDING_RIGHT = 120; // room for ruler handles + time labels + duration bracket
  const PADDING_TOP = 36;
  const PADDING_BOTTOM = 120;
  const TRACK_SLOT = 28;
  const GROUP_GAP = 20;

  const totalTracks = allGroups.reduce((s, g) => s + g.tracks.length, 0);
  const tracksWidth = totalTracks * TRACK_SLOT + Math.max(0, allGroups.length - 1) * GROUP_GAP;
  const svgWidth = PADDING_LEFT + PADDING_RIGHT + tracksWidth;
  const svgHeight = 600;
  const plotTop = PADDING_TOP;
  const plotBottom = svgHeight - PADDING_BOTTOM;
  const plotHeight = plotBottom - plotTop;
  const plotWidth = tracksWidth;

  // REVERSED: 00:00 at bottom, 24:00 at top
  const minToY = (m: number) => plotBottom - (m / TOTAL_MINS) * plotHeight;

  const yTicks: number[] = [];
  for (let m = 0; m <= TOTAL_MINS; m += 120) yTicks.push(m);
  const formatTime = (m: number) => {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h.toString().padStart(2, '0')}:${Math.round(mm).toString().padStart(2, '0')}`;
  };

  // Place tracks & build trackCenterX map
  type Placed = {track: Track; x: number};
  const placed: Placed[] = [];
  let cursor = PADDING_LEFT;
  const groupBands: {dimension: string; startX: number; endX: number}[] = [];
  const trackCenterX = new Map<string, number>(); // key: "dimension:trackkey"

  allGroups.forEach((group, gi) => {
    const groupStartX = cursor;
    group.tracks.forEach((t) => {
      trackCenterX.set(`${group.dimension}:${t.key}`, cursor + TRACK_SLOT / 2);
      placed.push({track: t, x: cursor});
      cursor += TRACK_SLOT;
    });
    groupBands.push({dimension: group.dimension, startX: groupStartX, endX: cursor});
    if (gi < allGroups.length - 1) cursor += GROUP_GAP;
  });

  // Build connected-line paths + hover zones for ALL dimensions
  function buildLinePath(entries: SeriesEntry[], dim: string): string {
    if (entries.length === 0) return '';
    const pts: string[] = [];
    for (const entry of entries) {
      const cx = trackCenterX.get(`${dim}:${entry.value}`);
      if (cx === undefined) continue;
      const y1 = minToY(entry.startMin);
      const y2 = minToY(entry.endMin);
      pts.push(pts.length === 0 ? `M ${cx} ${y1}` : `L ${cx} ${y1}`);
      pts.push(`L ${cx} ${y2}`);
    }
    return pts.join(' ');
  }

  function buildHoverZones(entries: SeriesEntry[], dim: string) {
    return entries
      .map((entry) => {
        const cx = trackCenterX.get(`${dim}:${entry.value}`);
        if (cx === undefined) return null;
        const y1 = minToY(entry.startMin);
        const y2 = minToY(entry.endMin);
        return {
          x: cx - TRACK_SLOT / 2, y: Math.min(y1, y2), w: TRACK_SLOT,
          h: Math.max(4, Math.abs(y2 - y1)),
          label: entry.label,
          dimension: dim,
          seg: {startMin: entry.startMin, endMin: entry.endMin, label: entry.label},
        };
      })
      .filter(Boolean) as any[];
  }

  const dimensionLines = useMemo(() => {
    return Object.entries(dimensionEntries).map(([dim, entries]) => ({
      dimension: dim,
      path: buildLinePath(entries, dim),
      zones: buildHoverZones(entries, dim),
      entries,
    }));
  }, [dimensionEntries, trackCenterX]);

  const showTooltip = (e: React.MouseEvent, label: string, seg: Segment) => {
    const rect = (e.currentTarget as SVGElement).ownerSVGElement!.getBoundingClientRect();
    setHover({
      x: e.clientX - rect.left + 12,
      y: e.clientY - rect.top + 12,
      text: `${label}\n${formatTime(seg.startMin)} – ${formatTime(seg.endMin)}`,
    });
  };

  const yToMin = (y: number) => {
    const clamped = Math.max(plotTop, Math.min(plotBottom, y));
    return ((plotBottom - clamped) / plotHeight) * TOTAL_MINS;
  };

  const yToTime = (y: number) => formatTime(Math.round(yToMin(y)));

  // Find which entries a ruler at a given y intersects
  const findIntersections = (rulerY: number) => {
    const rulerMin = Math.round(yToMin(rulerY));
    const hits: { cx: number; label: string; dimension: string }[] = [];
    Object.entries(dimensionEntries).forEach(([dim, entries]) => {
      entries.forEach((entry) => {
        if (rulerMin >= entry.startMin && rulerMin <= entry.endMin) {
          const cx = trackCenterX.get(`${dim}:${entry.value}`);
          if (cx !== undefined) {
            hits.push({ cx, label: entry.label, dimension: dim });
          }
        }
      });
    });
    return hits;
  };

  const handleRulerMouseDown = useCallback(
    (which: 1 | 2) => (e: React.MouseEvent) => {
      e.preventDefault();
      setDraggingRuler(which);
    },
    [],
  );

  const handleSvgMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!draggingRuler || !svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const rawY = e.clientY - rect.top;
      const clamped = Math.max(plotTop, Math.min(plotBottom, rawY));
      if (draggingRuler === 1) setRuler1Y(clamped);
      else setRuler2Y(clamped);
    },
    [draggingRuler, plotTop, plotBottom],
  );

  const handleSvgMouseUp = useCallback(() => setDraggingRuler(null), []);

  const effectiveRuler1 = showRulers ? (ruler1Y ?? minToY(480)) : null;
  const effectiveRuler2 = showRulers ? (ruler2Y ?? minToY(1020)) : null;

  return (
    <div className="w-full bg-white rounded-lg border border-gray-accent p-2" style={{position: 'relative'}}>
      <div className="flex items-center justify-center gap-6 mb-1">
        <h2 className="text-lg font-bold">
          {lang === 'EN' ? 'Daily Overview (HVB)' : 'Dagsöversikt (HVB)'}
        </h2>
        {/* Legend inline with title */}
        {allGroups.map((g) => (
          <div key={g.dimension} className="flex items-center gap-1 text-xs">
            <span
              className="inline-block"
              style={{width: 10, height: 10, background: DIMENSION_COLORS[g.dimension], borderRadius: 2}}
            />
            <span>{DIMENSION_LABELS[g.dimension][lang]}</span>
          </div>
        ))}
      </div>

      <div style={{overflowX: 'auto', display: 'flex', justifyContent: 'center'}}>
        <svg
          ref={svgRef}
          width={svgWidth}
          height={svgHeight}
          style={{display: 'block', userSelect: 'none', cursor: draggingRuler ? 'ns-resize' : undefined}}
          onMouseMove={handleSvgMouseMove}
          onMouseUp={handleSvgMouseUp}
          onMouseLeave={() => {
            setHover(null);
            setDraggingRuler(null);
          }}>

          {/* Plot background */}
          <rect x={PADDING_LEFT} y={plotTop} width={plotWidth} height={plotHeight} fill="#fafafa" stroke="#e5e7eb" />

          {/* Group background bands */}
          {groupBands.map((g) => (
            <rect
              key={g.dimension}
              x={g.startX - 2}
              y={plotTop}
              width={g.endX - g.startX + 4}
              height={plotHeight}
              fill={DIMENSION_COLORS[g.dimension]}
              opacity={0.05}
            />
          ))}

          {/* Horizontal grid + Y-axis labels */}
          {yTicks.map((m) => {
            const y = minToY(m);
            return (
              <g key={m}>
                <line x1={PADDING_LEFT} x2={PADDING_LEFT + plotWidth} y1={y} y2={y} stroke="#d1d5db" strokeDasharray="3 3" />
                <text x={PADDING_LEFT - 8} y={y + 4} textAnchor="end" fontSize={11} fill="#374151">
                  {formatTime(m)}
                </text>
              </g>
            );
          })}

          {/* Vertical separators */}
          {groupBands.slice(0, -1).map((g, i) => {
            const next = groupBands[i + 1];
            const x = (g.endX + next.startX) / 2;
            return <line key={`sep-${i}`} x1={x} x2={x} y1={plotTop} y2={plotBottom} stroke="#9ca3af" strokeWidth={1} />;
          })}

          {/* Group titles */}
          {groupBands.map((g) => {
            const cx = (g.startX + g.endX) / 2;
            return (
              <text key={`gt-${g.dimension}`} x={cx} y={plotTop - 12} textAnchor="middle" fontSize={12} fontWeight={700} fill={DIMENSION_COLORS[g.dimension]}>
                {DIMENSION_LABELS[g.dimension][lang]}
              </text>
            );
          })}

          {/* Track column guides + labels (all dimensions) */}
          {placed.map(({track, x}) => (
            <g key={`col-${track.dimension}-${track.key}`}>
              <line x1={x + TRACK_SLOT / 2} x2={x + TRACK_SLOT / 2} y1={plotTop} y2={plotBottom} stroke="#e5e7eb" strokeWidth={1} />
              <text
                x={x + TRACK_SLOT / 2}
                y={plotBottom + 12}
                fontSize={10}
                fill="#111827"
                transform={`rotate(45 ${x + TRACK_SLOT / 2} ${plotBottom + 12})`}>
                {track.label}
              </text>
            </g>
          ))}

          {/* Full-height column hover zones for dimension focus */}
          {placed.map(({track, x}) => (
            <rect
              key={`fhz-${track.dimension}-${track.key}`}
              x={x} y={plotTop} width={TRACK_SLOT} height={plotHeight}
              fill="transparent"
              onMouseEnter={() => setFocusDimension(track.dimension)}
              onMouseLeave={() => setFocusDimension(null)}
              style={{cursor: 'pointer'}}
            />
          ))}

          {/* Connected lines + dots + hover zones for ALL dimensions */}
          {dimensionLines.map(({dimension, path, zones, entries}) => (
            <g key={`line-${dimension}`}>
              {/* The line */}
              {path && (
                <path d={path} fill="none" stroke={DIMENSION_COLORS[dimension]} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
              )}
              {/* Dots at each entry start */}
              {entries.map((entry, i) => {
                const cx = trackCenterX.get(`${dimension}:${entry.value}`);
                if (cx === undefined) return null;
                return (
                  <circle key={`dot-${dimension}-${i}`} cx={cx} cy={minToY(entry.startMin)} r={3.5} fill={DIMENSION_COLORS[dimension]} stroke="white" strokeWidth={1.5} />
                );
              })}
              {/* Invisible hover zones */}
              {zones.map((z: any, i: number) => (
                <rect
                  key={`hz-${dimension}-${i}`}
                  x={z.x} y={z.y} width={z.w} height={z.h}
                  fill="transparent"
                  onMouseMove={(e) => { showTooltip(e, z.label, z.seg); setFocusDimension(z.dimension); }}
                  onMouseLeave={() => { setHover(null); setFocusDimension(null); }}
                  style={{cursor: 'pointer'}}
                />
              ))}
            </g>
          ))}

          {/* Rulers */}
          {[effectiveRuler1, effectiveRuler2].map((rulerY, ri) => {
            if (rulerY === null) return null;
            const intersections = findIntersections(rulerY);
            const rulerIdx = (ri + 1) as 1 | 2;
            return (
              <g key={`ruler-${ri}`}>
                {/* Ruler line */}
                <line x1={PADDING_LEFT} x2={PADDING_LEFT + plotWidth} y1={rulerY} y2={rulerY} stroke="#3b82f6" strokeWidth={2} />

                {/* Intersection diamond markers */}
                {intersections.map((hit, hi) => (
                  <rect
                    key={`dia-${ri}-${hi}`}
                    x={hit.cx - 5} y={rulerY - 5} width={10} height={10}
                    fill={DIMENSION_COLORS[hit.dimension]} stroke="white" strokeWidth={1.5}
                    transform={`rotate(45 ${hit.cx} ${rulerY})`}
                  />
                ))}

                {/* Drag handle with time */}
                <rect
                  x={PADDING_LEFT + plotWidth + 4} y={rulerY - 11}
                  width={52} height={22} rx={4}
                  fill="#3b82f6"
                  style={{cursor: 'ns-resize'}}
                  onMouseDown={handleRulerMouseDown(rulerIdx)}
                />
                <text
                  x={PADDING_LEFT + plotWidth + 30} y={rulerY + 4}
                  fontSize={11} fontWeight={700} fill="white"
                  textAnchor="middle"
                  style={{pointerEvents: 'none'}}
                >
                  {yToTime(rulerY)}
                </text>
              </g>
            );
          })}

          {/* Duration between rulers */}
          {effectiveRuler1 !== null && effectiveRuler2 !== null && (() => {
            const min1 = Math.round(yToMin(effectiveRuler1));
            const min2 = Math.round(yToMin(effectiveRuler2));
            const diff = Math.abs(min2 - min1);
            const hrs = Math.floor(diff / 60);
            const mins = diff % 60;
            const label = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
            const midY = (effectiveRuler1 + effectiveRuler2) / 2;
            return (
              <g>
                {/* Bracket line on right side */}
                <line x1={PADDING_LEFT + plotWidth + 60} x2={PADDING_LEFT + plotWidth + 60} y1={effectiveRuler1} y2={effectiveRuler2} stroke="#3b82f6" strokeWidth={1.5} />
                <line x1={PADDING_LEFT + plotWidth + 56} x2={PADDING_LEFT + plotWidth + 60} y1={effectiveRuler1} y2={effectiveRuler1} stroke="#3b82f6" strokeWidth={1.5} />
                <line x1={PADDING_LEFT + plotWidth + 56} x2={PADDING_LEFT + plotWidth + 60} y1={effectiveRuler2} y2={effectiveRuler2} stroke="#3b82f6" strokeWidth={1.5} />
                {/* Duration label */}
                <rect x={PADDING_LEFT + plotWidth + 63} y={midY - 10} width={label.length * 7 + 8} height={20} rx={4} fill="#3b82f6" />
                <text x={PADDING_LEFT + plotWidth + 67} y={midY + 4} fontSize={11} fontWeight={700} fill="white" style={{pointerEvents: 'none'}}>
                  {label}
                </text>
              </g>
            );
          })()}

          {/* Plot border */}
          <rect x={PADDING_LEFT} y={plotTop} width={plotWidth} height={plotHeight} fill="none" stroke="#9ca3af" />

          {/* Ruler intersection tooltips – rendered last so they're always on top */}
          {/* Non-focused tooltips first (dimmed) */}
          {[effectiveRuler1, effectiveRuler2].map((rulerY, ri) => {
            if (rulerY === null) return null;
            const intersections = findIntersections(rulerY);
            return intersections.map((hit, hi) => {
              const isFocused = focusDimension !== null && hit.dimension === focusDimension;
              const isDimmed = focusDimension !== null && !isFocused;
              if (isFocused) return null; // render focused ones on top in next pass
              const tw = Math.max(hit.label.length * 7 + 12, 48);
              const tx = Math.max(tw / 2, Math.min(svgWidth - tw / 2, hit.cx));
              return (
                <g key={`tip-${ri}-${hi}`} opacity={isDimmed ? 0.3 : 1}>
                  <rect
                    x={tx - tw / 2} y={rulerY - 34} width={tw} height={22}
                    rx={4} fill="rgba(17,24,39,0.92)"
                  />
                  <text x={tx} y={rulerY - 19} fontSize={11} fill="white" fontWeight={600} textAnchor="middle">
                    {hit.label}
                  </text>
                </g>
              );
            });
          })}
          {/* Focused tooltips on top (larger, highlighted) */}
          {focusDimension !== null && [effectiveRuler1, effectiveRuler2].map((rulerY, ri) => {
            if (rulerY === null) return null;
            const intersections = findIntersections(rulerY);
            return intersections.filter(h => h.dimension === focusDimension).map((hit, hi) => {
              const tw = Math.max(hit.label.length * 8 + 16, 56);
              const tx = Math.max(tw / 2, Math.min(svgWidth - tw / 2, hit.cx));
              return (
                <g key={`ftip-${ri}-${hi}`}>
                  <rect
                    x={tx - tw / 2} y={rulerY - 38} width={tw} height={26}
                    rx={5} fill={DIMENSION_COLORS[hit.dimension]} stroke="white" strokeWidth={1}
                  />
                  <text x={tx} y={rulerY - 21} fontSize={12} fill="white" fontWeight={700} textAnchor="middle">
                    {hit.label}
                  </text>
                </g>
              );
            });
          })}
        </svg>
      </div>

      {/* Tooltip (hidden when rulers active – ruler tooltips take over) */}
      {hover && !showRulers && (
        <div
          style={{
            position: 'absolute',
            left: hover.x,
            top: hover.y,
            background: 'rgba(17,24,39,0.92)',
            color: 'white',
            padding: '6px 8px',
            borderRadius: 4,
            fontSize: 12,
            pointerEvents: 'none',
            whiteSpace: 'pre-line',
            zIndex: 20,
          }}>
          {hover.text}
        </div>
      )}

      {allGroups.length === 0 && (
        <div className="text-center text-gray-500 mt-4">
          {lang === 'EN' ? 'No data to display' : 'Ingen data att visa'}
        </div>
      )}
    </div>
  );
};

export default HVBCombinedGraph;
